import { StrictMode } from 'react';
import ReactDOM from 'react-dom/client';
import { ModalsProvider } from '@mantine/modals';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import '@mantine/dropzone/styles.css';
import 'mantine-datatable/styles.css';
import '@mantine/notifications/styles.css';
import '@mantine/spotlight/styles.css';
import '@mantine/carousel/styles.css';
import App from './App.tsx';
import { Notifications } from '@mantine/notifications';
import './sentry.ts';
import { AppConfirmModal } from './components/modal/AppConfirmModal.tsx';
import { ThemeProvider } from './context/ThemeProvider.tsx';
import './index.css';
import axios from 'axios';

const getResponseStatus = (error: unknown) =>
  axios.isAxiosError(error) ? error.response?.status : undefined;

const MAX_RETRY_COUNT = 3;

const shouldRetryQuery = (failureCount: number, error: Error) => {
  const status = getResponseStatus(error);
  if (status === 403 || status === 404) return false;
  return failureCount < MAX_RETRY_COUNT;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetryQuery,
    },
  },
});

async function enableMocking() {
  if (import.meta.env.VITE_ENABLE_MSW !== 'true') {
    return;
  }

  const { worker } = await import('./mocks/browser');

  // `worker.start()` returns a Promise that resolves
  // once the Service Worker is up and ready to intercept requests.
  return worker.start({
    onUnhandledRequest: 'bypass',
    serviceWorker: {
      url: '/mockServiceWorker.js',
      options: {
        // Narrow down the scope of the pages that the worker can control.
        // scope: '/v2',
      },
    },
  });
}

enableMocking().then(() => {
  ReactDOM.createRoot(document.getElementById('root')).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <Notifications />
          <ModalsProvider
            modals={{
              confirmModal: AppConfirmModal,
            }}
          >
            <App />
          </ModalsProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </StrictMode>,
  );
});
